<template>
    <div
        :id="identifier"
        ref="mapContainer"
        class="map-container flex justify-center"
    >
        <div class="absolute top-1 w-full flex justify-center">
            <slot :name="Positions.TOP" />
        </div>
        <div class="absolute bottom-1 w-full flex justify-center">
            <slot :name="Positions.BOTTOM" />
        </div>
        <div
            ref="topLeftControl"
            :class="buttonGroupStyles"
        >
            <slot :name="Positions.TOP_LEFT" />
        </div>
        <div
            ref="topRightControl"
            :class="buttonGroupStyles"
        >
            <slot :name="Positions.TOP_RIGHT" />
        </div>
        <div
            ref="bottomLeftControl"
            :class="buttonGroupStyles"
        >
            <slot :name="Positions.BOTTOM_LEFT" />
        </div>

    </div>
</template>

<script
    setup
    lang="ts"
>
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import 'maplibre-gl/dist/maplibre-gl.css';

import { generateUUID } from "@ionic/cli/lib/utils/uuid";
// @ts-ignore - No types available for @mapbox/mapbox-gl-draw
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { GeoJSON } from 'geojson';
import { extent } from "geojson-bounds";
import { GeolocateControl, LngLatBounds, Map, MapTouchEvent, MapMouseEvent, NavigationControl, Popup, type ControlPosition } from 'maplibre-gl';
import type { PropType } from 'vue';
import { onMounted, ref, useSlots, watch } from 'vue';
import { CustomControl } from './custom-control.js';
import { setSources } from './sources.js';
import { MapSettings, PopupDefinition, Positions } from './types.js';
import { collapseAttributionControl } from './utils.js';

const props = defineProps({
    identifier: {
        type: String,
        required: false,
        default: generateUUID()
    },
    settings: {
        type: Object as PropType<MapSettings>,
        required: false,
        default: () => ({ style: 'https://demotiles.maplibre.org/style.json' })
    },
    showNavigation: {
        type: [Boolean, String] as PropType<false | ControlPosition>,
        required: false,
        default: Positions.TOP_LEFT
    },
    showGeolocate: {
        type: [Boolean, String] as PropType<false | ControlPosition>,
        required: false,
        default: Positions.TOP_LEFT
    },
    showDraw: {
        type: [Boolean, String] as PropType<false | ControlPosition>,
        required: false,
        default: Positions.TOP_RIGHT
    },
    popups: {
        type: Object as PropType<PopupDefinition[]>,
        required: false,
        default: () => [] as PopupDefinition[]
    },
    inputGeoJson: {
        type: Object as PropType<GeoJSON>,
        default: undefined
    },
    sources: {
        type: Object
    },
    layers: {
        type: Array
    },
    popupLayers: {
        type: Array<string>
    }
});

const emit = defineEmits(["onDraw", "onFirstIdle"]);

const buttonGroupStyles = "maplibregl-ctrl maplibregl-ctrl-group";

//template refs
const mapContainer = ref();
const topLeftControl = ref();
const topRightControl = ref();
const bottomLeftControl = ref();
let draw: MapboxDraw;

const mapLibre = ref<Map | null>(null);
const resizeMapNextTick = ref(true);

let touchStartPosition = {
    x: null as null | number,
    y: null as null | number
};
let touchEndPosition = {
    x: null as null | number,
    y: null as null | number
};

const slots = useSlots();

const initNavigation = () => {
    if (mapLibre.value && props.showNavigation) {
        mapLibre.value.addControl(new NavigationControl({
            showCompass: true,
            showZoom: true
        }), props.showNavigation);
    }
};

const initGeolocate = () => {
    if (mapLibre.value && props.showGeolocate) {
        mapLibre.value.addControl(new GeolocateControl({
            positionOptions: {
                enableHighAccuracy: true,
            },
            trackUserLocation: true,
            showAccuracyCircle: true,
        }), props.showGeolocate);
    }
};

const initDraw = () => {
    if (mapLibre.value && props.showDraw) {
        // https://maplibre.org/maplibre-gl-js/docs/examples/mapbox-gl-draw/ 
        // @ts-ignore
        MapboxDraw.constants.classes.CONTROL_BASE = 'maplibregl-ctrl';
        // @ts-ignore
        MapboxDraw.constants.classes.CONTROL_PREFIX = 'maplibregl-ctrl-';
        // @ts-ignore
        MapboxDraw.constants.classes.CONTROL_GROUP = 'maplibregl-ctrl-group';
        // @ts-ignore
        MapboxDraw.constants.classes.ATTRIBUTION = 'maplibregl-ctrl-attrib';
        // @ts-ignore
        MapboxDraw.constants.classes.CANVAS = 'maplibregl-canvas';

        draw = new MapboxDraw({
            displayControlsDefault: false,
            controls: {
                polygon: true,
                trash: true
            }
            // custom draw style: (requires to define all!! active, inactive, midpoint, vertex,..)
            /*styles: [
                {
                    id: 'gl-draw-polygon-stroke-inactive',
                    type: 'line',
                    filter: ['all', ['==', 'active', 'false'],
                                    ['==', '$type', 'Polygon']
                        ],
                    layout: {
                        'line-cap': 'round',
                        'line-join': 'round'
                    },
                    paint: {
                        'line-color': '#FF0000',
                        'line-width': 2
                    }
                },
            ]*/
        });
        mapLibre.value.on("draw.create", () => handleDrawUpdate());
        mapLibre.value.on("draw.delete", () => handleDrawUpdate());
        mapLibre.value.on("draw.update", () => handleDrawUpdate());
        // @ts-ignore
        mapLibre.value.addControl(draw, props.showDraw);
    }
};

const resizeMap = () => {
    if (resizeMapNextTick.value && mapLibre.value) {
        mapLibre.value.resize();
        resizeMapNextTick.value = false;
    }
};

let currentPopup: null | Popup = null;

const onClick = (event: MapMouseEvent | MapTouchEvent) => {
    currentPopup?.remove();
    props.popups.forEach((popup) => {
        popup.onHide(event);
        if (popup.onShow(event) && mapLibre.value) {
            currentPopup = new Popup()
                .setLngLat(event.lngLat)
                .setDOMContent(popup.element)
                // @ts-ignore
                .addTo(mapLibre.value);
        }
    });
};

const addLayerEvents = () => {
    if (props.popupLayers) {
        for (const myLayerName of props.popupLayers.values()) {
            mapLibre.value?.on('click', myLayerName, onClick);

            // Change the cursor to a pointer when the mouse is over the layer.
            mapLibre.value?.on('mouseenter', myLayerName, () => {
                if (mapLibre.value) {
                    mapLibre.value.getCanvas().classList.add('point'); // not working with scoped class!!
                }
            });
            // Change it back to a pointer when it leaves.
            mapLibre.value?.on('mouseleave', myLayerName, () => {
                if (mapLibre.value) {
                    mapLibre.value.getCanvas().classList.remove('point');
                }
            });
        }
    }
};

const addEvents = () => {
    if (mapLibre.value) {
        //mapLibre.value?.on('click', onClick);
        mapLibre.value.once("load", () => {
            props.inputGeoJson && draw.add(props.inputGeoJson);
            collapseAttributionControl(mapContainer.value); //TODO: also do in visibilityWatcher
        });
        mapLibre.value?.on('load', async () => {
            if (props.sources && mapLibre.value) {
                const map = mapLibre.value;
                // @ts-ignore type mismatch with map although both are Map types
                setSources(map, props.sources);
            }
            if (props.layers) {
                for (const layerElement of props.layers) {
                    mapLibre.value?.addLayer(layerElement as any);
                }
            }
            addLayerEvents();

        });
        mapLibre.value.once("idle", () => {
            if (props.inputGeoJson && mapLibre.value) {
                const bounds = extent(props.inputGeoJson); // array of coordinates in the order [West, South, East, North] [14.191531327367358, 47.39284495150875, 14.222609577492932, 47.413490587966294]
                const bbox = new LngLatBounds([bounds[0], bounds[1]], [bounds[2], bounds[3]]); // sw, ne
                mapLibre.value.fitBounds(bbox, { padding: 150 }); // [[-79, 43], [-73, 45]]
            }
            emit("onFirstIdle", mapLibre.value);
        });
        mapLibre.value.on("idle", () => {
            resizeMap();
        });

        //TODO: check if mobile device

        mapLibre.value?.on("touchstart", (e: any) => {
            touchStartPosition = e.point;
        });
        mapLibre.value?.on("touchend", (e: MapTouchEvent) => {
            touchEndPosition = e.point;
            //console.log("touchend", e);
            if (touchStartPosition.x === touchEndPosition.x &&
                touchStartPosition.y === touchEndPosition.y) {
                const canvasWidth = mapLibre.value?.getCanvas().offsetWidth;
                if (canvasWidth && mapLibre.value && props.showDraw) {
                    // shift event to correct position for test-popup - works - but how to fire again to draw-layers?
                    //console.log("canvas width: "+canvasWidth);
                    e.point.x = e.point.x + (canvasWidth / 2);
                    e.lngLat = mapLibre.value.unproject(e.point);
                }
                onClick(e);
            }
        });
    }
};

const addCustomButtons = () => {
    //TODO: allow to add multiple control groups
    if (mapLibre.value) {
        if (slots[Positions.TOP_LEFT]) {
            mapLibre.value.addControl(new CustomControl(topLeftControl.value), Positions.TOP_LEFT);
        }
        if (slots[Positions.TOP_RIGHT]) {
            mapLibre.value.addControl(new CustomControl(topRightControl.value), Positions.TOP_RIGHT);
        }
        if (slots[Positions.BOTTOM_LEFT]) {
            mapLibre.value.addControl(new CustomControl(bottomLeftControl.value), Positions.BOTTOM_LEFT);
        }
    }
};

const initMapLibre = () => {
    if (mapContainer.value) {
        mapLibre.value = new Map({
            container: mapContainer.value,
            style: props.settings.style,
            center: props.settings.center ?? undefined, // ususally given in style file
            zoom: props.settings.zoom ?? undefined,
            bearing: props.settings.bearing,
            pitch: props.settings.pitch,
            maxZoom: props.settings.maxZoom,
            minZoom: props.settings.minZoom,
            maxBounds: props.settings.maxBounds,
            interactive: props.settings.interactive ?? true,
            //attributionControl: props.settings.attributionControl ?? undefined,
            logoPosition: props.settings.logoPosition ?? 'bottom-left',
        });
    }
};

watch(() => props.sources, (newSources) => {
    if (newSources) {
        // @ts-ignore type mismatch with map although both are Map types
        setSources(mapLibre.value, newSources);
    }
}, { deep: true });

onMounted(() => {
    initMapLibre();
    initDraw();
    addEvents();
    initNavigation();
    initGeolocate();
    addCustomButtons();

    // const testmarker = new Marker()
    //     .setLngLat([80, 30])
    //     .addTo(mapLibre.value);
});

function handleDrawUpdate() {
    emit('onDraw', draw.getAll());
}


</script>



<style>
/*
    .map-container {
    }
*/
.maplibregl-ctrl-top-left,
.maplibregl-ctrl-top-right,
.maplibregl-ctrl-bottom-left,
.maplibregl-ctrl-bottom-right,
.top-1,
.bottom-1 {
    z-index: 2000;
}

.point {
    cursor: pointer;
}

.maplibregl-ctrl-group,
.maplibregl-ctrl-group button {
    transform: translateZ(0);
    filter: blur(0);
}

.maplibregl-popup-close-button {
    right: 4px;
}
</style>